import { useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  ExtendedCard,
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
} from "@NP-Webber/crm-kit";
import { shellApi, type TaskDto } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import {
  CheckCircleOutlinedIcon,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
} from "../../ui/appIcons";

const STATUS_LABELS: Record<string, string> = {
  pending: "ממתין",
  in_progress: "בתהליך",
  completed: "הושלם",
  cancelled: "בוטל",
};

const PRIORITY_LABELS: Record<string, string> = {
  low: "נמוכה",
  normal: "רגילה",
  high: "גבוהה",
  urgent: "דחוף",
};

export type TaskCardProps = {
  open: boolean;
  task: TaskDto | null;
  onClose: () => void;
  onChanged?: () => void;
};

export function TaskCard({ open, task, onClose, onChanged }: TaskCardProps) {
  const [active, setActive] = useState<TaskDto | null>(task);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("pending");
  const [priority, setPriority] = useState("normal");
  const [dueAt, setDueAt] = useState("");

  useEffect(() => {
    if (open && task) {
      setActive(task);
      setError(null);
      setEditOpen(false);
      setDeleteOpen(false);
    }
    if (!open) {
      setEditOpen(false);
      setDeleteOpen(false);
      setError(null);
    }
  }, [open, task]);

  const detailsFields = useMemo(
    () => [
      { key: "title", label: "כותרת", value: active?.title ?? "" },
      {
        key: "status",
        label: "סטטוס",
        value: STATUS_LABELS[active?.status ?? ""] ?? active?.status ?? "—",
      },
      {
        key: "priority",
        label: "עדיפות",
        value: PRIORITY_LABELS[active?.priority ?? ""] ?? active?.priority ?? "—",
      },
      {
        key: "dueAt",
        label: "יעד",
        value: active?.dueAt
          ? new Date(active.dueAt).toLocaleString("he-IL")
          : "—",
      },
      {
        key: "description",
        label: "תיאור",
        value: active?.description?.trim() || "—",
      },
    ],
    [active],
  );

  const openEdit = () => {
    if (!active) return;
    setTitle(active.title);
    setDescription(active.description ?? "");
    setStatus(active.status);
    setPriority(active.priority);
    setDueAt(active.dueAt ? active.dueAt.slice(0, 16) : "");
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!active || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await shellApi.updateTask(active.id, {
        title: title.trim(),
        description: description.trim() || null,
        status,
        priority,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      });
      setActive(updated);
      setEditOpen(false);
      onChanged?.();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await shellApi.updateTask(active.id, { status: "completed" });
      setActive(updated);
      onChanged?.();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      await shellApi.deleteTask(active.id);
      setDeleteOpen(false);
      onChanged?.();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message);
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !active) {
    return null;
  }

  const headerActions = [
    {
      icon: <EditOutlinedIcon />,
      tooltip: "עריכה",
      onClick: openEdit,
    },
    ...(active.status !== "completed"
      ? [
          {
            icon: <CheckCircleOutlinedIcon />,
            tooltip: "סמן הושלם",
            onClick: () => {
              void handleComplete();
            },
          },
        ]
      : []),
    {
      icon: <DeleteOutlinedIcon />,
      tooltip: "מחיקה",
      onClick: () => setDeleteOpen(true),
    },
  ];

  return (
    <>
      <ExtendedCard
        key={active.id}
        open
        onClose={onClose}
        title={active.title}
        subtitle={STATUS_LABELS[active.status] ?? active.status}
        detailsFields={detailsFields}
        headerActions={headerActions}
        detailsHeaderExtra={
          error ? <FormAlert severity="error">{error}</FormAlert> : null
        }
        feedSources={{ activities: [], tasks: [], notes: [] }}
        hideFeedTab
        showDashboard={false}
        defaultTab={0}
      />

      <FormDialog
        open={editOpen}
        onClose={() => {
          if (!busy) setEditOpen(false);
        }}
        size="md"
      >
        <FormCard
          title="עריכת משימה"
          error={error}
          onSave={() => {
            void handleSave();
          }}
          onCancel={() => setEditOpen(false)}
          saveLabel="שמירה"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={!title.trim()}
        >
          <FormSection>
            <FormGrid cols={2}>
              <FormField
                label="כותרת"
                required
                value={title}
                onChange={setTitle}
                disabled={busy}
                span={2}
              />
              <FormField
                label="סטטוס"
                type="select"
                value={status}
                onChange={setStatus}
                disabled={busy}
                options={Object.entries(STATUS_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
              <FormField
                label="עדיפות"
                type="select"
                value={priority}
                onChange={setPriority}
                disabled={busy}
                options={Object.entries(PRIORITY_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
              <FormField
                label="יעד"
                type="datetime-local"
                value={dueAt}
                onChange={setDueAt}
                disabled={busy}
                span={2}
              />
              <FormField
                label="תיאור"
                type="textarea"
                rows={4}
                value={description}
                onChange={setDescription}
                disabled={busy}
                span={2}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => {
          if (!busy) setDeleteOpen(false);
        }}
        title="מחיקת משימה"
        message={`למחוק את המשימה "${active.title}"?`}
        confirmText="מחיקה"
        cancelText="ביטול"
        loading={busy}
        onConfirm={() => {
          void handleDelete();
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}
